// SPDX-License-Identifier: AGPL-3.0-or-later

/**
 * Live Halloween catalog keys. Parked art stays on disk and is not listed here.
 *
 * Order is load-bearing: `rollHalloweenCostumeId` indexes this array with
 * `hashStringToIndex`. Reordering reassigns every Randomize wearer.
 */
export const HALLOWEEN_COSTUME_IDS = [
	'halloween.witch-hat-1',
	'halloween.witch-hat-8',
	'halloween.witch-hat-3',
	'halloween.ring-witch-hat',
	'halloween.party-hat-wizard',
	'halloween.party-hat-jester',
	'halloween.party-hat-jester-pompoms',
	'halloween.party-hat-cowboy',
	'halloween.pirate-hat',
	'halloween.pirate-hat-2',
	'halloween.nurse-hat',
	'halloween.the-pan',
	'halloween.devil-horns',
	'halloween.bunny-ears',
	'halloween.ring-devil-horn-tail',
	'halloween.catface',
	'halloween.frankenstein-face',
] as const;

export type HalloweenCostumeId = (typeof HALLOWEEN_COSTUME_IDS)[number];

/**
 * Retired art that may still exist on `users` / wearer rows. Stats keep these
 * ids as themselves; the write path must not accept them.
 */
export const PARKED_HALLOWEEN_COSTUME_IDS = [
	'halloween.bunny-ears-flowers',
	'halloween.ring-web',
	'halloween.elf-ear',
] as const;

const PARKED_COSTUME_ID_SET = new Set<string>(PARKED_HALLOWEEN_COSTUME_IDS);

/** Stored sentinel: roll a live catalog costume every 2 hours. Not a catalog row. */
export const HALLOWEEN_COSTUME_RANDOM_ID = 'halloween.random' as const;

/** Public display-gate mask. Never a stored value. */
export const HALLOWEEN_COSTUME_NONE_ID = 'halloween.none' as const;

export const HALLOWEEN_COSTUME_PACK = 'halloween' as const;

export type HalloweenCostumeChoiceId = HalloweenCostumeId | typeof HALLOWEEN_COSTUME_RANDOM_ID;

export const COSTUME_FLIP_SUFFIX = ':flip' as const;

/** Dye ids in picker order. Order is load-bearing for Randomize color rolls. */
export const HALLOWEEN_COSTUME_DYE_IDS = ['black', 'orange', 'purple', 'green', 'blood'] as const;

export type HalloweenCostumeDyeId = (typeof HALLOWEEN_COSTUME_DYE_IDS)[number];

/** Swatch id for clearing wearer color (costume-specific default paints). */
export const COSTUME_COLOR_DEFAULT_ID = 'default' as const;

/** Stored sentinel: roll Default + dyes every 2 hours, one hour after costume Randomize. */
export const COSTUME_COLOR_RANDOM_ID = 'random' as const;

/** Default + dyes. Randomize rolls among these (never the Randomize sentinel itself). */
export const COLOR_RANDOM_POOL: ReadonlyArray<string | null> = [null, ...HALLOWEEN_COSTUME_DYE_IDS];

/** Wearer Randomize rolls stay put this long, then everyone re-resolves the same window. */
export const COSTUME_RANDOM_PERIOD_MS = 2 * 60 * 60 * 1000;

/** Color Randomize uses the same period, one hour after costume Randomize. */
export const COLOR_RANDOM_OFFSET_MS = 60 * 60 * 1000;

const LIVE_COSTUME_ID_SET = new Set<string>(HALLOWEEN_COSTUME_IDS);

/**
 * Byte-identical to the original app hash. Uses `| 0` wraparound and `Math.abs`.
 * Changing this silently reassigns every user's random costume.
 */
export function hashStringToIndex(value: string, modulo: number): number {
	if (modulo <= 0) return 0;
	let hash = 0;
	for (let i = 0; i < value.length; i++) {
		hash = (hash * 31 + value.charCodeAt(i)) | 0;
	}
	return Math.abs(hash) % modulo;
}

export function costumeRandomWindow(now = Date.now()): number {
	return Math.floor(now / COSTUME_RANDOM_PERIOD_MS);
}

export function colorRandomWindow(now = Date.now()): number {
	return Math.floor((now - COLOR_RANDOM_OFFSET_MS) / COSTUME_RANDOM_PERIOD_MS);
}

export function pickFromPool<T>(
	bareUserId: string,
	salt: string,
	pool: ReadonlyArray<T>,
	now = Date.now(),
	offsetMs = 0,
): T {
	const window = Math.floor((now - offsetMs) / COSTUME_RANDOM_PERIOD_MS);
	const index = hashStringToIndex(`${bareUserId}:${salt}:${window}`, pool.length);
	return pool[index] as T;
}

/**
 * Randomize hashes a user snowflake. A trailing `:flip` is stripped so a stored
 * costume choice cannot throw from a React render (picker / avatar resolver).
 */
function normalizeBareUserId(bareUserId: string): string {
	const value = typeof bareUserId === 'string' ? bareUserId : String(bareUserId);
	return value.endsWith(COSTUME_FLIP_SUFFIX) ? value.slice(0, -COSTUME_FLIP_SUFFIX.length) : value;
}

/** `bareUserId` is a user snowflake, never a stored costume id (suffixed or otherwise). */
export function rollHalloweenCostumeId(bareUserId: string, now = Date.now()): HalloweenCostumeId {
	return pickFromPool(normalizeBareUserId(bareUserId), 'costume', HALLOWEEN_COSTUME_IDS, now);
}

/** `bareUserId` is a user snowflake, never a stored costume id (suffixed or otherwise). */
export function rollHalloweenCostumeColorId(bareUserId: string, now = Date.now()): string | null {
	return pickFromPool(normalizeBareUserId(bareUserId), 'color', COLOR_RANDOM_POOL, now, COLOR_RANDOM_OFFSET_MS);
}

export interface ParsedCostumeId {
	baseId: string | null;
	flipped: boolean;
	known: boolean;
}

/**
 * Stats-oriented parse. Unlike the app's `parseCostumeChoice`, this keeps the raw
 * base for unrecognised junk (`known: false`) so never-picked (`baseId: null`) and
 * junk can be distinguished. Does not throw.
 */
export function parseCostumeId(stored: string | null | undefined): ParsedCostumeId {
	if (stored == null || stored === '') {
		return {baseId: null, flipped: false, known: false};
	}
	const flipped = stored.endsWith(COSTUME_FLIP_SUFFIX);
	const baseId = flipped ? stored.slice(0, -COSTUME_FLIP_SUFFIX.length) : stored;
	if (baseId === '') {
		return {baseId: '', flipped, known: false};
	}
	const known = baseId === HALLOWEEN_COSTUME_RANDOM_ID || LIVE_COSTUME_ID_SET.has(baseId);
	return {baseId, flipped, known};
}

export function formatCostumeId(baseId: string, flipped: boolean): string {
	return flipped ? `${baseId}${COSTUME_FLIP_SUFFIX}` : baseId;
}

/** Catalog row only — not random, not a `:flip` suffix. Existing app callers parse first. */
export function isHalloweenCostumeId(id: string | null | undefined): id is HalloweenCostumeId {
	return id != null && LIVE_COSTUME_ID_SET.has(id);
}

export function isHalloweenCostumeChoiceId(id: string): id is HalloweenCostumeChoiceId {
	return id === HALLOWEEN_COSTUME_RANDOM_ID || isHalloweenCostumeId(id);
}

export function isParkedHalloweenCostumeId(id: string | null | undefined): boolean {
	return id != null && PARKED_COSTUME_ID_SET.has(id);
}

/** Write-path allowlist: live catalog, random sentinel, optional `:flip`. Not `halloween.none`. */
export function isEquippableCostumeId(stored: string | null | undefined): boolean {
	if (stored == null) return true;
	const parsed = parseCostumeId(stored);
	return parsed.known && parsed.baseId != null && stored === formatCostumeId(parsed.baseId, parsed.flipped);
}

const EQUIPABLE_COLOR_IDS = new Set<string>([
	COSTUME_COLOR_DEFAULT_ID,
	COSTUME_COLOR_RANDOM_ID,
	...HALLOWEEN_COSTUME_DYE_IDS,
]);

export function isEquippableCostumeColorId(stored: string | null | undefined): boolean {
	if (stored == null) return true;
	return EQUIPABLE_COLOR_IDS.has(stored);
}
